import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { T } from '@/components/common/T';
import { goToLanguage } from '@/lib/locale';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/common/UserAvatar';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Menu, Globe, Sun, Moon, ChevronDown, ShoppingCart, Sparkles, User, LogOut, LayoutDashboard, UserPlus } from 'lucide-react';
import silaLogo from '@/assets/brand/sila-logo.png';
import { usePublicTheme } from '@/context/PublicThemeContext';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';

// Rotta di atterraggio per ruolo/tenant (coerente con AuthContext.destFor).
function destFor(u) {
  if (!u) return '/area';
  if (u.role === 'admin') return '/admin';
  if (u.role === 'customer') return '/cliente';
  if (u.role === 'staff') return '/staff-area';
  if (u.role === 'stakeholder' && u.tenant_type === 'guida') return '/area/parco';
  return '/area';
}

function roleLabelOf(u) {
  if (!u) return '';
  if (u.role === 'admin') return 'Amministratore';
  if (u.role === 'customer') return 'Cliente';
  if (u.role === 'staff') return 'Staff';
  if (u.role === 'stakeholder') return u.tenant_type === 'guida' ? 'Guida' : 'Partner';
  return 'Account';
}

const NAV_LINK_CLS = 'whitespace-nowrap rounded text-[13px] font-medium text-foreground/75 transition-colors duration-200 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none';
const NAV_CARET_CLS = 'inline-flex items-center gap-1 whitespace-nowrap rounded text-[13px] font-medium text-foreground/75 outline-none transition-colors duration-200 hover:text-foreground focus-visible:text-foreground';

// Voce di navigazione reale (link, link+dropdown, dropdown puro o ancora).
function NavEntry({ l }) {
  if (l.children && l.to) {
    return (
      <div className="inline-flex items-center gap-1">
        <Link to={l.to} data-testid={`header-nav-${l.label}`} className={NAV_LINK_CLS}>{l.label}</Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label={`${l.label} — altre pagine`} data-testid={`header-nav-${l.label}-caret`}
              className="inline-flex items-center rounded text-foreground/55 outline-none transition-colors duration-200 hover:text-foreground focus-visible:text-foreground">
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {l.children.map((c) => (
              <DropdownMenuItem key={c.label} asChild>
                <Link to={c.to} data-testid={`header-nav-${c.label}`}>{c.label}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  if (l.children) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`header-nav-${l.label}`} className={NAV_CARET_CLS}>
            {l.label}<ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {l.children.map((c) => (
            <DropdownMenuItem key={c.label} asChild>
              <Link to={c.to} data-testid={`header-nav-${c.label}`}>{c.label}</Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (l.to) return <Link to={l.to} data-testid={`header-nav-${l.label}`} className={NAV_LINK_CLS}>{l.label}</Link>;
  return <a href={l.href} data-testid={`header-nav-${l.label}`} className={NAV_LINK_CLS}>{l.label}</a>;
}

// Copia STATICA (senza Radix, senza data-testid) usata solo per MISURARE la larghezza.
function MeasureEntry({ l }) {
  if (l.children && l.to) {
    return (
      <div className="inline-flex items-center gap-1">
        <span className={NAV_LINK_CLS}>{l.label}</span>
        <span className="inline-flex items-center text-foreground/55"><ChevronDown className="h-3.5 w-3.5" /></span>
      </div>
    );
  }
  if (l.children) {
    return <span className={NAV_CARET_CLS}>{l.label}<ChevronDown className="h-3.5 w-3.5" /></span>;
  }
  return <span className={NAV_LINK_CLS}>{l.label}</span>;
}

// Navigazione desktop responsive: mostra quante più voci entrano; le rimanenti
// confluiscono in un dropdown "Altro" così NESSUNA voce viene mai tagliata.
function DesktopNav({ navLinks }) {
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);

  const recompute = useCallback(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const avail = row.clientWidth;
    const gap = 12; // gap-x-3 = 0.75rem
    const widths = Array.from(measure.children).map((c) => c.getBoundingClientRect().width);
    const total = widths.reduce((a, w) => a + w, 0) + gap * Math.max(0, widths.length - 1);
    if (total <= avail + 0.5) { setVisibleCount(widths.length); return; }
    const ALTRO = 66 + gap; // spazio riservato al trigger "Altro"
    let used = 0, count = 0;
    for (let i = 0; i < widths.length; i++) {
      const add = widths[i] + (i > 0 ? gap : 0);
      if (used + add + ALTRO <= avail) { used += add; count += 1; } else break;
    }
    setVisibleCount(Math.max(1, count));
  }, [navLinks.length]);

  useLayoutEffect(() => {
    recompute();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && rowRef.current) {
      ro = new ResizeObserver(() => recompute());
      ro.observe(rowRef.current);
    }
    window.addEventListener('resize', recompute);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', recompute); };
  }, [recompute]);

  useEffect(() => { recompute(); }, [navLinks, recompute]);

  const visible = navLinks.slice(0, visibleCount);
  const overflow = navLinks.slice(visibleCount);

  return (
    <nav className="relative mx-2 hidden min-w-0 flex-1 items-center xl:flex" aria-label="Navigazione principale" data-testid="header-desktop-nav">
      {/* Riga di misura nascosta: contiene SEMPRE tutte le voci (no Radix, no testid). */}
      <div ref={measureRef} aria-hidden="true" className="pointer-events-none invisible absolute -z-10 flex items-center gap-x-3">
        {navLinks.map((l) => <MeasureEntry key={`m-${l.label}`} l={l} />)}
      </div>
      {/* Riga visibile */}
      <div ref={rowRef} className="flex w-full items-center gap-x-3 overflow-hidden">
        {visible.map((l) => <NavEntry key={l.label} l={l} />)}
        {overflow.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-nav-altro" aria-label="Altre voci di menu" className={NAV_CARET_CLS}>
                <T>Altro</T><ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {overflow.map((l) => (
                l.children ? (
                  <React.Fragment key={l.label}>
                    {l.to ? (
                      <DropdownMenuItem asChild><Link to={l.to} data-testid={`header-nav-${l.label}`}>{l.label}</Link></DropdownMenuItem>
                    ) : (
                      <DropdownMenuLabel>{l.label}</DropdownMenuLabel>
                    )}
                    {l.children.map((c) => (
                      <DropdownMenuItem key={c.label} asChild className="pl-6"><Link to={c.to} data-testid={`header-nav-${c.label}`}>{c.label}</Link></DropdownMenuItem>
                    ))}
                  </React.Fragment>
                ) : l.to ? (
                  <DropdownMenuItem key={l.label} asChild><Link to={l.to} data-testid={`header-nav-${l.label}`}>{l.label}</Link></DropdownMenuItem>
                ) : (
                  <DropdownMenuItem key={l.label} asChild><a href={l.href} data-testid={`header-nav-${l.label}`}>{l.label}</a></DropdownMenuItem>
                )
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  );
}

export const PublicHeader = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = usePublicTheme();
  const { count, openMiniCart } = useCart() || { count: 0, openMiniCart: () => {} };
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isLogged = !!(user && user.role);

  const handleLogout = async () => {
    setOpen(false);
    try { await logout?.(); } catch (e) { /* noop */ }
    navigate('/');
  };

  const changeLang = (lng) => { goToLanguage(lng); };

  const navLinks = [
    { label: t('nav.il_progetto'), to: '/il-progetto' },
    { label: t('nav.parco_sila'), to: '/il-parco-della-sila', children: [
      { label: 'La Sila', to: '/la-sila' },
    ] },
    { label: t('nav.tour_borghi'), to: '/tour-nei-borghi' },
    { label: t('nav.stays'), children: [
      { label: 'Vacanze', to: '/alloggi' },
      { label: 'Case in vendita', to: '/case-in-vendita' },
    ] },
    { label: t('nav.eat'), to: '/dove-mangiare' },
    { label: t('nav.experience'), to: '/esperienze' },
    { label: t('nav.educational'), children: [
      { label: t('nav.corsi_online'), to: '/educational/corsi-online' },
      { label: t('nav.in_sila'), to: '/educational/in-sila' },
    ] },
    { label: t('nav.eventi'), to: '/eventi' },
    { label: 'Servizi', to: '/servizi' },
    { label: t('nav.shop'), to: '/shop' },
    { label: t('nav.magazine'), to: '/blog' },
    { label: t('nav.contatti'), to: '/contatti' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/70 backdrop-blur-md" data-testid="public-header">
      <div className="mx-auto flex h-16 w-full max-w-[1536px] items-center justify-between gap-3 px-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center" data-testid="header-logo" aria-label="Sila Experiences — home">
          <img
            src={silaLogo}
            alt="Sila Experiences — Riscopri la tua natura attraverso la Natura della Sila"
            className="h-11 w-auto dark:rounded-md dark:bg-white/95 dark:px-1.5 dark:py-1"
            width="215"
            height="110"
          />
        </Link>

        <DesktopNav navLinks={navLinks} />

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Componi la vacanza (CTA) */}
          <Button asChild size="sm" variant="outline" className="hidden gap-1.5 rounded-full border-primary/40 sm:inline-flex" data-testid="header-trip-button">
            <Link to="/vacanza">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="hidden lg:inline"><T>Componi la vacanza</T></span>
              <span className="lg:hidden">Vacanza</span>
            </Link>
          </Button>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5" data-testid="header-language-switch" aria-label="Cambia lingua">
                <Globe className="h-4 w-4" /><span className="uppercase hidden sm:inline">{i18n.language}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeLang('it')} data-testid="language-it-option">🇮🇹 Italiano</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLang('en')} data-testid="language-en-option">🇬🇧 English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLang('es')} data-testid="language-es-option">🇪🇸 Español</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme */}
          <Button variant="ghost" size="icon" onClick={toggle} aria-label={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'} data-testid="header-theme-toggle">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Cart */}
          <Button variant="ghost" size="icon" className="relative" data-testid="header-cart-button" aria-label={count > 0 ? `Carrello, ${count} articoli` : 'Carrello vuoto'} onClick={openMiniCart}>
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums"
                data-testid="header-cart-badge">{count}</span>
            )}
          </Button>

          {/* Account (loggato) oppure Area riservata (non loggato) */}
          <div className="hidden sm:block">
            {isLogged ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full gap-2 pl-1 pr-3" data-testid="header-account-button" aria-label="Apri il menu account">
                    <UserAvatar name={user.name || user.email} src={user.avatar_url} className="h-7 w-7 text-xs" fallbackClassName="bg-primary text-primary-foreground" />
                    <span className="hidden max-w-[130px] truncate text-[13px] font-medium md:inline" data-testid="header-account-name">{user.name || user.email}</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="truncate font-semibold">{user.name || 'Il mio account'}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">{roleLabelOf(user)}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={destFor(user)} data-testid="header-my-area-link"><LayoutDashboard className="mr-2 h-4 w-4" />{t('nav.my_area', 'La mia area')}</Link>
                  </DropdownMenuItem>
                  {user.role === 'customer' && (
                    <DropdownMenuItem asChild>
                      <Link to="/cliente/profilo" data-testid="header-my-profile-link"><User className="mr-2 h-4 w-4" />{t('nav.my_profile', 'Il mio profilo')}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="header-logout" className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />{t('nav.logout', 'Esci')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="rounded-full gap-1" data-testid="header-reserved-area-button">
                    {t('nav.reserved')} <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Clienti</DropdownMenuLabel>
                  <DropdownMenuItem asChild><Link to="/cliente/accedi" data-testid="reserved-customer-login"><User className="mr-2 h-4 w-4" />Accedi</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/cliente/registrati" data-testid="reserved-customer-register"><UserPlus className="mr-2 h-4 w-4" />Registrati</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Stakeholder <span className="font-normal text-muted-foreground">· Experience, Ospitalità, Ristoranti, eCommerce</span></DropdownMenuLabel>
                  <DropdownMenuItem asChild><Link to="/partner/accedi" data-testid="reserved-stakeholder-login">Accedi</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/partner/registrati" data-testid="reserved-stakeholder-register">Registrati</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Guida del Parco</DropdownMenuLabel>
                  <DropdownMenuItem asChild><Link to="/guida/accedi" data-testid="reserved-guida-login">Accedi</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/guida/registrati" data-testid="reserved-guida-register">Registrati</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Staff</DropdownMenuLabel>
                  <DropdownMenuItem asChild><Link to="/staff/accedi" data-testid="reserved-staff-login">Accedi</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/staff/registrati" data-testid="reserved-staff-register">Registrati</Link></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="xl:hidden" data-testid="header-mobile-menu-button" aria-label="Apri il menu di navigazione"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="sila-public flex w-80 max-w-[85vw] flex-col overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display text-left">Sila Experiences</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                <Link to="/vacanza" onClick={() => setOpen(false)} data-testid="header-mobile-trip"
                  className="mb-2 flex items-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-base font-semibold text-primary-foreground transition-opacity hover:opacity-90">
                  <Sparkles className="h-4 w-4" />Componi la tua vacanza
                </Link>
                {navLinks.map((l) => (
                  l.children ? (
                    <div key={l.label} className="py-1">
                      {l.to ? (
                        <Link to={l.to} onClick={() => setOpen(false)} data-testid={`header-mobile-nav-${l.label}`}
                          className="flex items-center gap-1 rounded-lg px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground">{l.label}</Link>
                      ) : (
                        <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{l.label}</p>
                      )}
                      {l.children.map((c) => (
                        <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                          className="block rounded-lg px-5 py-2.5 text-base font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground">{c.label}</Link>
                      ))}
                    </div>
                  ) : l.to ? (
                    <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
                      className="rounded-lg px-3 py-2.5 text-base font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground">{l.label}</Link>
                  ) : (
                    <a key={l.label} href={l.href} onClick={() => setOpen(false)}
                      className="rounded-lg px-3 py-2.5 text-base font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground">{l.label}</a>
                  )
                ))}
              </div>
              <div className="mt-6 space-y-2 border-t border-border pt-6">
                {isLogged ? (
                  <>
                    <div className="mb-1 flex items-center gap-3 px-1">
                      <UserAvatar name={user.name || user.email} src={user.avatar_url} className="h-9 w-9 text-sm" fallbackClassName="bg-primary text-primary-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold" data-testid="header-mobile-account-name">{user.name || user.email}</p>
                        <p className="truncate text-xs text-muted-foreground">{roleLabelOf(user)}</p>
                      </div>
                    </div>
                    <Button asChild className="w-full rounded-full">
                      <Link to={destFor(user)} onClick={() => setOpen(false)} data-testid="header-mobile-my-area"><LayoutDashboard className="mr-2 h-4 w-4" />{t('nav.my_area', 'La mia area')}</Link>
                    </Button>
                    <Button variant="outline" className="w-full rounded-full text-destructive hover:text-destructive" data-testid="header-mobile-logout" onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />{t('nav.logout', 'Esci')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild className="w-full rounded-full">
                      <Link to="/cliente/accedi" onClick={() => setOpen(false)} data-testid="header-mobile-customer-login"><User className="mr-2 h-4 w-4" />Accedi come cliente</Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full rounded-full">
                      <Link to="/cliente/registrati" onClick={() => setOpen(false)} data-testid="header-mobile-customer-register"><UserPlus className="mr-2 h-4 w-4" />Registrati come cliente</Link>
                    </Button>
                    <div className="!my-3 border-t border-border" />
                    <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stakeholder · Experience, Ospitalità, Ristoranti, eCommerce</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button asChild variant="outline" className="rounded-full"><Link to="/partner/accedi" onClick={() => setOpen(false)} data-testid="header-mobile-stakeholder-login">Accedi</Link></Button>
                      <Button asChild variant="outline" className="rounded-full"><Link to="/partner/registrati" onClick={() => setOpen(false)} data-testid="header-mobile-stakeholder-register">Registrati</Link></Button>
                    </div>
                    <p className="mt-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Guida del Parco</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button asChild variant="outline" className="rounded-full"><Link to="/guida/accedi" onClick={() => setOpen(false)} data-testid="header-mobile-guida-login">Accedi</Link></Button>
                      <Button asChild variant="outline" className="rounded-full"><Link to="/guida/registrati" onClick={() => setOpen(false)} data-testid="header-mobile-guida-register">Registrati</Link></Button>
                    </div>
                    <p className="mt-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Staff</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button asChild variant="outline" className="rounded-full"><Link to="/staff/accedi" onClick={() => setOpen(false)} data-testid="header-mobile-staff-login">Accedi</Link></Button>
                      <Button asChild variant="outline" className="rounded-full"><Link to="/staff/registrati" onClick={() => setOpen(false)} data-testid="header-mobile-staff-register">Registrati</Link></Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
